import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AiAnalysisView from "@/components/AiAnalysisView";
import { GitCompare, Loader2, ZoomIn, ZoomOut, Sparkles } from "lucide-react";

function Pane({ item, label }) {
  const [zoom, setZoom] = useState(1);
  return (
    <div className="flex-1 min-w-0" data-testid={`compare-pane-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium truncate">{item ? `${item.exam_type} · ${item.exam_date}` : `Exame ${label}`}</p>
        {item && (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setZoom((z) => Math.max(1, z - 0.25))} data-testid={`compare-zoomout-${label}`}><ZoomOut className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} data-testid={`compare-zoomin-${label}`}><ZoomIn className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      <div className="h-72 rounded-md border border-border bg-black overflow-auto flex items-center justify-center">
        {item ? (
          <img src={item.image_base64 ? `data:${item.mime_type};base64,${item.image_base64}` : item.image_url} alt={item.exam_type}
            style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 0.15s" }}
            className="max-h-72 object-contain" />
        ) : <span className="text-white/40 text-sm">Selecione um exame</span>}
      </div>
    </div>
  );
}

export default function ImagingCompareDialog({ patientId, items }) {
  const [open, setOpen] = useState(false);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const a = items.find((i) => i.id === aId);
  const b = items.find((i) => i.id === bId);

  const run = async () => {
    if (!aId || !bId || aId === bId) { toast.error("Selecione dois exames diferentes"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/patients/${patientId}/imaging/compare?a_id=${aId}&b_id=${bId}`);
      setResult(data.result);
    } catch (e) { toast.error("Falha ao comparar exames"); }
    finally { setLoading(false); }
  };

  const evo = result?.evolucao_geral;
  const evoColor = evo === "melhora" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : evo === "piora" ? "bg-red-100 text-red-700 border-red-300"
    : "bg-amber-100 text-amber-700 border-amber-300";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={items.length < 2} data-testid="compare-imaging-btn">
          <GitCompare className="w-4 h-4 mr-2" /> Comparar exames
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle>Comparar exames de imagem</DialogTitle>
          <DialogDescription>Veja dois exames lado a lado (com zoom) e gere uma análise comparativa por IA.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Exame A</Label>
              <Select value={aId} onValueChange={setAId}>
                <SelectTrigger data-testid="compare-select-a"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.exam_type} · {i.exam_date}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Exame B</Label>
              <Select value={bId} onValueChange={setBId}>
                <SelectTrigger data-testid="compare-select-b"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.exam_type} · {i.exam_date}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-4">
            <Pane item={a} label="A" />
            <Pane item={b} label="B" />
          </div>

          <Button onClick={run} disabled={loading} className="w-full" data-testid="run-compare-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Analisar comparação por IA
          </Button>

          {result && (
            <Card className="p-5 border-border shadow-none" data-testid="compare-result">
              <div className="flex items-center gap-2 mb-3">
                <h4 className="font-heading font-medium">Comparação IA</h4>
                {evo && <Badge variant="outline" className={evoColor}>Evolução: {evo}</Badge>}
              </div>
              <p className="text-sm mb-3">{result.resumo}</p>
              {result.diferencas?.length > 0 && (
                <div className="space-y-2 mb-3">
                  {result.diferencas.map((d, i) => (
                    <div key={i} className="text-sm border-l-2 border-border pl-3">
                      <span className="font-medium">{d.regiao}:</span> {d.descricao}
                      {d.evolucao && <Badge variant="secondary" className="ml-2 text-xs">{d.evolucao}</Badge>}
                    </div>
                  ))}
                </div>
              )}
              {result.impressao && <p className="text-sm"><span className="font-medium">Impressão:</span> {result.impressao}</p>}
              <p className="text-xs text-muted-foreground italic mt-3">{result.disclaimer}</p>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
